/* eslint-disable */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Sparkles,
  Crown,
  Calendar,
  ShieldCheck,
  Pencil,
  LogOut,
  UserRound,
  X,
} from "lucide-react";
import { Card, Pill, Avatar, Button } from "../ui";
import { resolveAvatarUrl } from "../utils/avatarUrl";
import EndBoardMemberDialog from "./EndBoardMemberDialog";
import "./MemberCard.css";

function telLink(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(-10);
  return d ? `tel:+91${d}` : "#";
}

function waLink(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(-10);
  return d ? `https://wa.me/91${d}` : "#";
}

// Map a board role onto the closest design-system Pill variant.
// Falls back to "neutral" for anything we don't have a hue for.
function rolePillVariant(role) {
  switch ((role || "").toUpperCase()) {
    case "PRESIDENT":
    case "VICE_PRESIDENT":
      return "warning";
    case "TREASURER":
    case "JOINT_TREASURER":
      return "success";
    case "SECRETARY":
    case "JOINT_SECRETARY":
      return "p2";
    case "DIRECTOR":
      return "neutral";
    default:
      return "neutral";
  }
}

export default function MemberCard({
  variant = "board", // 'board' | 'elder' | 'team'
  member,
  dimmed = false,
  isAdmin = false,
  onEdit,
  onPromote, // team only
  onRemove, // team only
  onChanged,
}) {
  const [showEndDialog, setShowEndDialog] = useState(false);
  const u = member.user || {};
  const phone = u.phone;
  const canCall = !dimmed && phone && !/[•]/.test(phone);
  const isTeam = variant === "team";
  const isLead = isTeam && member.role === "LEAD";

  const rolePill =
    variant === "board" ? (
      <Pill variant={rolePillVariant(member.role)} className="vsbm-role-pill">
        <Crown size={11} strokeWidth={2.4} />
        {member.role_label}
      </Pill>
    ) : variant === "elder" ? (
      <Pill variant="warning" className="vsbm-role-pill">
        <Sparkles size={11} strokeWidth={2.4} /> Elder
      </Pill>
    ) : isLead ? (
      <Pill variant="success" className="vsbm-role-pill">
        <Crown size={11} strokeWidth={2.4} /> Lead
      </Pill>
    ) : (
      <Pill variant="neutral" className="vsbm-role-pill">
        Member
      </Pill>
    );

  return (
    <Card
      className={`vsbm-card vsbm-card-wide ${dimmed ? "dimmed" : ""}`}
      padding="16px 20px"
    >
      <div className="vsbm-row">
        <Avatar
          name={u.name || ""}
          imageUrl={u.avatar_url ? resolveAvatarUrl(u.avatar_url) : undefined}
          size={52}
          className="vsbm-avatar"
        />

        <div className="vsbm-main">
          <div className="vsbm-name-line">
            <h3>{u.name || "Unnamed"}</h3>
            {rolePill}
          </div>
          <div className="vsbm-sub">
            {u.villa_number && (
              <span className="vsbm-villa">{u.villa_number}</span>
            )}
            {member.term_label && (
              <span className="vsbm-term">
                <Calendar size={11} strokeWidth={2.2} />
                {dimmed ? "Served " : "Term "}
                {member.term_label}
              </span>
            )}
            {dimmed && (
              <span className="vsbm-served">
                <ShieldCheck size={11} strokeWidth={2.2} /> Served — thank you
              </span>
            )}
          </div>
          {variant === "elder" && member.bio && (
            <p className="vsbm-resp">{member.bio}</p>
          )}
        </div>

        {!dimmed && (
          <div className="vsbm-actions">
            {u.id && (
              <Link
                to={`/profile/${u.id}`}
                className="vsbm-action"
                aria-label="View profile"
                title="View profile"
              >
                <UserRound size={16} />
              </Link>
            )}
            <a
              href={telLink(phone)}
              className={`vsbm-action ${!canCall ? "disabled" : ""}`}
              aria-disabled={!canCall}
              aria-label="Call"
              title="Call"
            >
              <Phone size={16} />
            </a>
            <a
              href={waLink(phone)}
              target="_blank"
              rel="noreferrer"
              className={`vsbm-action vsbm-action-wa ${!canCall ? "disabled" : ""}`}
              aria-disabled={!canCall}
              aria-label="WhatsApp"
              title="WhatsApp"
            >
              <MessageCircle size={16} />
            </a>
          </div>
        )}

        {isAdmin && !dimmed && isTeam && (
          <div className="vsbm-admin-actions">
            {!isLead && onPromote && (
              <Button
                variant="ghost"
                size="sm"
                className="vsbm-icon-btn"
                onClick={() => onPromote(member)}
                aria-label="Promote to Lead"
                title="Promote to Lead"
              >
                <Crown size={13} />
              </Button>
            )}
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="vsbm-icon-btn"
                onClick={() => onRemove(member)}
                aria-label="Remove from team"
                title="Remove from team"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        )}

        {isAdmin && !dimmed && !isTeam && (
          <div className="vsbm-admin-actions">
            <Button
              variant="ghost"
              size="sm"
              className="vsbm-icon-btn"
              onClick={onEdit}
              aria-label="Edit"
              title="Edit"
            >
              <Pencil size={13} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="vsbm-icon-btn"
              onClick={() => setShowEndDialog(true)}
              aria-label="Move out of board"
              title={variant === "elder" ? "End Elder term" : "Move out of board"}
            >
              <LogOut size={13} />
            </Button>
          </div>
        )}
      </div>

      {showEndDialog && (
        <EndBoardMemberDialog
          variant={variant}
          member={member}
          onClose={() => setShowEndDialog(false)}
          onEnded={() => {
            setShowEndDialog(false);
            onChanged && onChanged();
          }}
        />
      )}
    </Card>
  );
}
